import { Patient, Session } from '../types';
import { normalizeSession, normalizeSessions, sessionsArrayToMap, validateAndFixSessions } from './sessionNormalizer';
import { normalizeObjectTimestamps, normalizeTimestamp, normalizeAppointmentTimestamps } from './timestampNormalizer';
import { validateAndCleanBeforeSave } from './dataValidator';
import { safeGetItem, safeSetItem } from './safeStorage';
// Removido scheduleSyncToSupabase - agora gerenciado pelo RealtimeDataContext

// Chaves para armazenamento
const PATIENTS_KEY = 'psicosys_patients';
const SESSIONS_KEY = 'psicosys_sessions';
const APPOINTMENTS_KEY = 'psicosys_appointments';

// Interface para Appointment
interface Appointment {
  id: string;
  patientId: string;
  patientName: string;
  date: Date | string;
  time: string;
  notes?: string;
  sessionPrice?: number;
}

// Salvar pacientes
export const savePatients = (patients: Patient[]): void => {
  try {
    // Normalizar timestamps antes de salvar
    const normalizedPatients = patients.map((patient: any) => normalizeObjectTimestamps(patient));
    safeSetItem(PATIENTS_KEY, JSON.stringify(normalizedPatients));
    // Sincronização agora é gerenciada pelo RealtimeDataContext
  } catch (error) {
    console.error('Erro ao salvar pacientes:', error);
  }
};

// Carregar pacientes
export const loadPatients = (): Patient[] => {
  try {
    const data = safeGetItem(PATIENTS_KEY);
    if (data) {
      const patients = JSON.parse(data);
      // Normalizar timestamps ao carregar
      return patients.map((patient: any) => normalizeObjectTimestamps(patient));
    }
    return [];
  } catch (error) {
    console.error('Erro ao carregar pacientes:', error);
    return [];
  }
};

// Salvar sessões
export const saveSessions = (sessions: Record<string, Session[]>): void => {
  try {
    // Normalizar e converter o objeto de sessões para um array para compatibilidade com Supabase
    const normalizedSessions = Object.values(sessions).flat().map(normalizeSession);
    safeSetItem(SESSIONS_KEY, JSON.stringify(normalizedSessions));
    // Sincronização agora é gerenciada pelo RealtimeDataContext
  } catch (error) {
    console.error('Erro ao salvar sessões:', error);
  }
};

// Carregar sessões
export const loadSessions = (): Record<string, Session[]> => {
  try {
    const data = safeGetItem(SESSIONS_KEY);
    if (!data) return {};
    
    // Validar e corrigir dados antes de processar
    validateAndFixSessions();
    
    // Carregar dados corrigidos
    const correctedData = safeGetItem(SESSIONS_KEY);
    if (!correctedData) return {};
    
    // Normalizar e converter o array para o formato de objeto agrupado por patientId
    const sessionsArray = normalizeSessions(JSON.parse(correctedData));
    return sessionsArrayToMap(sessionsArray);
  } catch (error) {
    console.error('Erro ao carregar sessões:', error);
    return {};
  }
};

// Adicionar ou atualizar paciente
export const savePatient = (patient: Patient): void => {
  const patients = loadPatients();
  const existingIndex = patients.findIndex(p => p.id === patient.id);
  
  if (existingIndex >= 0) {
    patients[existingIndex] = patient;
  } else {
    patients.push(patient);
  }
  
  savePatients(patients);
};

// Adicionar sessão
export const addSession = (session: Session): void => {
  try {
    const sessions = loadSessions();
    
    // Preparar nova sessão
    const newSession = {
      ...session,
      createdAt: session.createdAt || normalizeTimestamp(new Date()),
      updated_at: normalizeTimestamp(new Date())
    };
    
    // Validar dados antes de salvar
    validateAndCleanBeforeSave(newSession, 'session');
    
    // Normalizar a nova sessão
    const normalizedSession = normalizeSession(newSession);
    
    if (!sessions[normalizedSession.patientId]) {
      sessions[normalizedSession.patientId] = [];
    }
    
    sessions[normalizedSession.patientId] = [normalizedSession, ...sessions[normalizedSession.patientId]];
    saveSessions(sessions);
    
    // Trigger an event to notify components about session changes
    window.dispatchEvent(new CustomEvent('storage', {
      detail: { key: SESSIONS_KEY }
    }));
  } catch (error) {
    console.error('Erro ao adicionar sessão:', error);
  }
};

// Atualizar sessão
export const updateSession = (session: Session): void => {
  try {
    const sessions = loadSessions();
    
    if (!sessions[session.patientId]) return;
    
    const index = sessions[session.patientId].findIndex(s => s.id === session.id);
    if (index >= 0) {
      // Preparar sessão atualizada
      const sessionToUpdate = {
        ...session,
        updated_at: normalizeTimestamp(new Date())
      };
      
      // Validar dados antes de salvar
      validateAndCleanBeforeSave(sessionToUpdate, 'session');
      
      // Normalizar e atualizar timestamp de modificação
      const normalizedSession = normalizeSession(sessionToUpdate);
      
      sessions[normalizedSession.patientId][index] = normalizedSession;
      saveSessions(sessions);
      
      // Trigger an event to notify components about session changes
      window.dispatchEvent(new CustomEvent('storage', {
        detail: { key: SESSIONS_KEY }
      }));
    }
  } catch (error) {
    console.error('Erro ao atualizar sessão:', error);
  }
};

// Excluir sessão
export const deleteSession = (patientId: string, sessionId: string): void => {
  try {
    const sessions = loadSessions();
    
    if (!sessions[patientId]) return;
    
    sessions[patientId] = sessions[patientId].filter(s => s.id !== sessionId);
    saveSessions(sessions);
    
    // Trigger an event to notify components about session changes
    window.dispatchEvent(new CustomEvent('storage', {
      detail: { key: SESSIONS_KEY }
    }));
  } catch (error) {
    console.error('Erro ao excluir sessão:', error);
  }
};

// Salvar agendamentos
export const saveAppointments = (appointments: Appointment[]): void => {
  try {
    // Normalizar timestamps antes de salvar, preservando formato HH:MM do campo time
    const normalizedAppointments = appointments.map(appointment => normalizeAppointmentTimestamps(appointment));
    safeSetItem(APPOINTMENTS_KEY, JSON.stringify(normalizedAppointments));
    // Sincronização agora é gerenciada pelo RealtimeDataContext
  } catch (error) {
    console.error('Erro ao salvar agendamentos:', error);
  }
};

// Carregar agendamentos
export const loadAppointments = (): Appointment[] => {
  try {
    const data = safeGetItem(APPOINTMENTS_KEY);
    if (data) {
      const appointments = JSON.parse(data);
      // Normalizar timestamps ao carregar, preservando formato HH:MM do campo time
      return appointments.map((appointment: any) => normalizeAppointmentTimestamps(appointment));
    }
    return [];
  } catch (error) {
    console.error('Erro ao carregar agendamentos:', error);
    return [];
  }
};

// Adicionar ou atualizar agendamento
export const saveAppointment = (appointment: Appointment): void => {
  const appointments = loadAppointments();
  const existingIndex = appointments.findIndex(a => a.id === appointment.id);
  
  if (existingIndex >= 0) {
    appointments[existingIndex] = appointment;
  } else {
    appointments.push(appointment);
  }
  
  saveAppointments(appointments);
};

// Excluir agendamento
export const deleteAppointment = (appointmentId: string): void => {
  const appointments = loadAppointments();
  const updatedAppointments = appointments.filter(a => a.id !== appointmentId);
  saveAppointments(updatedAppointments);
};